import React from "react";
import ReactModal from "react-modal";
import Select from "../../common/select";
import KeywordMeta from "../library/components/keyword-meta";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import { toast } from "react-toastify";
import axios, { errorHandler, responseHandler } from "../../requests/axios";
import Loader from "../../common/loader";

export default function EditPlaylistMetaDialog({ isOpen, onClose, playlist, additionalPayloadFields, updatePage }) {
  const [isLoading, setIsLoading] = React.useState(false);
  const adUrlRef = React.useRef();
  const defaultOptions = {
    ad_decision_url: additionalPayloadFields.ad_decision_url || playlist.ad_decision_server || "",
    label: playlist?.label || "",
    priority: playlist?.priority ?? 0,
    platform: playlist?.platform || "netgem",
    keywords: playlist?.tags ? playlist?.tags.map((kw) => ({ keyword: kw.tag })) : [],
  };
  const platformOptions = [
    { label: "HbbTV", value: "hbbtv" },
    { label: "Netgem", value: "netgem" },
  ];

  const [form, setForm] = React.useState({ ...defaultOptions });

  function updateForm(field, value) {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function validateMeta() {
    if (form.priority < 1) {
      toast.info("Priority must be at least 1");
      return false;
    }

    if (!form.ad_decision_url) {
      toast.error("Ad Decision Server URL is required");
      return false;
    }

    return true;
  }

  function onSave() {
    setIsLoading(true);
    if (!validateMeta()) {
      setIsLoading(false);
      return;
    }

    const payload = {
      ...additionalPayloadFields,
      ...form,
      tags: form.keywords ? form.keywords.map((kw) => kw.keyword) : form.tags,
    };

    axios
      .patch(`/api/avod-playlists/${payload.avod_playlist_guid}/versions/${payload.version}`, payload)
      .then(
        responseHandler((response) => {
          // success
          onClose();
          updatePage(response.data);
          toast.success(`Successfully updated`);
        }),
      )
      .catch(
        errorHandler((error) => {
          if (error.response.status === 422) {
            toast.error("Something went wrong...");
          } else if (error.response.status === 500 || error.response.status === 404) {
            toast.error("There was unexpected error. Please contact support.");
          } else if (error.response.status === 403) {
            // get custom error message from api
            toast.error(error.response.data);
          }
        }),
      )
      .finally(() => {
        setIsLoading(false);
      });
  }

  return (
    <ReactModal isOpen={isOpen} ariaHideApp={false}>
      <div className="v-react-modal__inner modal-width">
        <div className="v-react-modal__heading">Edit Playlist Meta</div>
        <div className="v-react-modal__body modal-body">
          <div className="modal-copy__dropdown-group">
            <div className="modal-copy__body__text">Ad Decision Server URL*</div>
            <div className="modal-copy__dropdown">
              <div className="modal-copy__dropdown-group modal-copy__dropdown-group--inner">
                {playlist?.status && playlist.status === "draft" ? (
                  <input
                    type="text"
                    name="ad_decision_url"
                    id="ad_decision_url"
                    value={form?.ad_decision_url ?? ""}
                    onChange={(e) => updateForm("ad_decision_url", e.target.value)}
                    ref={adUrlRef}
                  />
                ) : (
                  <input
                    type="text"
                    name="ad_decision_url"
                    id="ad_decision_url"
                    value={form?.ad_decision_url}
                    disabled
                    ref={adUrlRef}
                  />
                )}
                <button
                  className="btn btn--icon"
                  onClick={() => {
                    if (adUrlRef.current) {
                      navigator.clipboard.writeText(adUrlRef.current.value);
                      toast.info("Ad Server URL copied to clipboard");
                    }
                  }}
                  aria-label="Copy Ad Url"
                  data-balloon-pos="left"
                >
                  <ContentCopyRoundedIcon />
                </button>
              </div>
            </div>
          </div>

          <div className="modal-copy__dropdown-group">
            <div className="modal-copy__body__text">Label</div>
            <div className="modal-copy__dropdown">
              <input
                type="text"
                name="label"
                value={form.label}
                onChange={(e) => updateForm("label", e.target.value)}
              />
            </div>
          </div>

          <div className="modal-copy__dropdown-group">
            <div className="modal-copy__body__text">Priority</div>
            <div className="modal-copy__dropdown">
              <input
                type="number"
                name="priority"
                min={1}
                value={form.priority}
                onChange={(e) => updateForm("priority", Number(e.target.value))}
              />
            </div>
          </div>

          <div className="modal-copy__dropdown-group">
            <div className="modal-copy__body__text">Platform</div>
            <div className="modal-copy__dropdown">
              <Select
                name={"platform"}
                onChange={(input) => {
                  updateForm("platform", input.value);
                }}
                value={platformOptions.find((option) => option.value === form.platform)}
                options={platformOptions}
              />
            </div>
          </div>

          <div className="modal-copy__dropdown-group">
            <div className="modal-copy__body__text modal-copy__body__text--top">Tags</div>
            <div className="modal-copy__dropdown">
              <KeywordMeta
                metadata={{}}
                isEditing={true}
                updateForm={(input) => updateForm("keywords", input.keywords)}
                form={form}
                placeholder={"Select tags..."}
                noTitle={true}
              />
            </div>
          </div>
        </div>
        <div className="v-react-modal__footer">
          {isLoading ? (
            <Loader height={25} width={25} />
          ) : (
            <React.Fragment>
              <button className="btn btn--inverse-primary" onClick={onClose}>
                Cancel
              </button>
              <button className="btn btn--primary" onClick={onSave}>
                Save Changes
              </button>
            </React.Fragment>
          )}
        </div>
      </div>
    </ReactModal>
  );
}
